import { useCommandManager } from '@model-framework/command';
import type { GroupId, ModelId, StickyZoneId, ViewId } from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';
import { useGetTransformedVisibleAreaCenterPoint } from '@user/pages/ViewModelPage/VisibleAreaCenterPointProvider';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import { ViewsClipboardOperator, ViewsClipboardPayload } from '@view-model/application/clipboard';
import type { MLAPIPayload } from '@view-model/application/ml-api/MLAPIPayload';
import type { WindowEventManager } from '@view-model/application/shortcuts';
import type { StickyModel } from '@view-model/domain/model';
import { ModelContentsCollection } from '@view-model/domain/model';
import type { ViewEntity } from '@view-model/domain/view';
import { ViewCollection } from '@view-model/domain/view';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Point } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic';
import type { CreatingModelComment } from '@view-model/models/sticky/ModelComment';
import { StickyModelContentsView } from '@view-model/ui/components/Model';
import { ViewAnalysisSelectModal } from '@view-model/ui/components/View';
import { Fragment, useCallback, useState, memo, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { ConsistencyLinkTargetFrame } from './ConsistencyLinkTargetFrame';
import { useViewLogSender } from './ViewLogSender';
import { ViewPopupMenu } from './ViewPopupMenu';
import { ViewSelectionFrame } from './ViewSelectionFrame';
import { ViewTitleBar } from './ViewTitleBar';
import { MultiSelectionMode } from '@user/pages/ViewModelPage/MultiSelectionMode';
import { PositionSet } from '@view-model/models/common/PositionSet';
import { useOpenCloseState } from './useOpenCloseState';
import { ViewTitleBarTeleporter } from '@user/pages/ViewModelPage/teleporters';
import { ViewKey } from '@view-model/domain/key';
import { ClientSelectedItemCollectionLoader } from '@view-model/models/sticky/client-selected-items/ClientSelectedItemCollectionLoader';
import { ActionLogEventParamsContextProvider } from '@framework/action-log';
import type { DragContext } from '@model-framework/ui';
import { useHandler } from '@framework/hooks';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';
import { EditingDescriptionPanelContextProvider } from '@view-model/models/sticky/DescriptionPanel/hook/EditingDescriptionPanelContext';
import type { DragFileTarget, DragFileTargetActions } from '@model-framework/drag-file/DragFileArea';
import { useIsDebugMode } from '../../../../framework/utils';
import { AIVerificationInput } from './AIVerificationInput';
import { SVGToImage } from '@model-framework/image/SVGToImage';
import { useViewOperation } from '@view-model/ui/components/View/ViewContext';
import { MeaningPanelTitleBar } from './MeaningPanelTitleBar';

// NOTE: Propsの型を変更する場合には、propsAreEqual()も変更すること
type Props = {
  readonly: boolean;
  ownerGroupId: GroupId;
  currentUserProfile: UserPublicProfile;
  viewModel: ViewModelEntity;
  viewId: ViewId;
  view: ViewEntity;
  viewPosition: Point;
  model: StickyModel;
  isSelected: boolean;
  onSelectSingleView(viewId: ViewId): void;
  onToggleSelectedViews(viewId: ViewId): void;
  onDeselectAllViews(): void;
  setMultiSelectionMode(multiSelectionMode: MultiSelectionMode): void;
  windowEventManager: WindowEventManager;
  isLinkableTarget: boolean;
  multiSelectionMode: MultiSelectionMode;
  onClickViewModelLink(e: React.MouseEvent): void;
  modelCommentThreadId: string | null;
  stickyModelContentsOperation: StickyModelContentsOperation | undefined;
  creatingComment: CreatingModelComment | undefined;
  onCreatingCommentAdd(modelId: ModelId, position: Point): void;
  onCreatingCommentDrag(modelId: ModelId, dx: number, dy: number): void;
  onCreatingCommentDragEnd(modelId: ModelId): void;
  onCreatingCommentCancel(modelId: ModelId): void;
  onCreatingCommentSubmit(modelId: ModelId, creatingComment: CreatingModelComment): void;
  focusView: (viewId: string) => void;
  duplicateView: (payload: ViewsClipboardPayload) => void;
  openDeleteModal(): void;
  onDragStartView(viewId: ViewId): void;
  onDragView(context: DragContext): void;
  onDragEndView(): void;
  getViewRectOperations(): Record<ViewId, { view: ViewEntity; rect?: Rect; operation?: StickyModelContentsOperation }>;
  onZoneToView(sourceView: ViewEntity, operation: StickyModelContentsOperation): void;
  getGuardProps: GetGuardProps;
  dispatch: React.Dispatch<DragFileTargetActions>;
  dragFileTargetState: DragFileTarget;
  onToggleMeaningPanel: (meaningPanelViewId: ViewId) => void;
  onCloseMeaningPanel: (meaningPanelViewId: ViewId) => void;
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.readonly === nextProps.readonly &&
    prevProps.ownerGroupId === nextProps.ownerGroupId &&
    prevProps.currentUserProfile === nextProps.currentUserProfile &&
    prevProps.viewModel === nextProps.viewModel &&
    prevProps.viewId === nextProps.viewId &&
    prevProps.view.isEqual(nextProps.view) &&
    prevProps.viewPosition.isEqual(nextProps.viewPosition) &&
    prevProps.model.isEqual(nextProps.model) &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.onSelectSingleView === nextProps.onSelectSingleView &&
    prevProps.onToggleSelectedViews === nextProps.onToggleSelectedViews &&
    prevProps.onDeselectAllViews === nextProps.onDeselectAllViews &&
    prevProps.setMultiSelectionMode === nextProps.setMultiSelectionMode &&
    prevProps.windowEventManager === nextProps.windowEventManager &&
    prevProps.isLinkableTarget === nextProps.isLinkableTarget &&
    prevProps.multiSelectionMode === nextProps.multiSelectionMode &&
    prevProps.onClickViewModelLink === nextProps.onClickViewModelLink &&
    prevProps.modelCommentThreadId === nextProps.modelCommentThreadId &&
    prevProps.stickyModelContentsOperation === nextProps.stickyModelContentsOperation &&
    prevProps.creatingComment === nextProps.creatingComment &&
    prevProps.onCreatingCommentAdd === nextProps.onCreatingCommentAdd &&
    prevProps.onCreatingCommentDrag === nextProps.onCreatingCommentDrag &&
    prevProps.onCreatingCommentDragEnd === nextProps.onCreatingCommentDragEnd &&
    prevProps.onCreatingCommentCancel === nextProps.onCreatingCommentCancel &&
    prevProps.onCreatingCommentSubmit === nextProps.onCreatingCommentSubmit &&
    prevProps.focusView === nextProps.focusView &&
    prevProps.duplicateView === nextProps.duplicateView &&
    prevProps.openDeleteModal === nextProps.openDeleteModal &&
    prevProps.onDragStartView === nextProps.onDragStartView &&
    prevProps.onDragView === nextProps.onDragView &&
    prevProps.onDragEndView === nextProps.onDragEndView &&
    prevProps.getViewRectOperations === nextProps.getViewRectOperations &&
    prevProps.onZoneToView === nextProps.onZoneToView &&
    prevProps.getGuardProps === nextProps.getGuardProps &&
    prevProps.dispatch === nextProps.dispatch &&
    prevProps.dragFileTargetState === nextProps.dragFileTargetState
  );
};

type SwapperProps = {
  swap: boolean;
  a: React.ReactNode;
  b: React.ReactNode;
};

const Swapper: React.FC<SwapperProps> = ({ swap, a, b }: SwapperProps) => {
  // Fragmentにkeyを指定し再レンダリングさせないようにしている
  const compA = <Fragment key="a">{a}</Fragment>;
  const compB = <Fragment key="b">{b}</Fragment>;

  return <>{swap ? [compB, compA] : [compA, compB]}</>;
};

const ViewFn: React.FC<Props> = ({
  readonly,
  ownerGroupId,
  currentUserProfile,
  viewModel,
  view,
  viewPosition,
  model,
  isSelected,
  onToggleSelectedViews,
  onSelectSingleView,
  onDeselectAllViews,
  setMultiSelectionMode,
  windowEventManager,
  isLinkableTarget,
  multiSelectionMode,
  onClickViewModelLink,
  modelCommentThreadId,
  stickyModelContentsOperation,
  creatingComment,
  onCreatingCommentAdd,
  onCreatingCommentDrag,
  onCreatingCommentDragEnd,
  onCreatingCommentCancel,
  onCreatingCommentSubmit,
  focusView,
  duplicateView,
  openDeleteModal,
  onDragStartView,
  onDragView,
  onDragEndView,
  getViewRectOperations,
  onZoneToView,
  getGuardProps,
  dispatch,
  dragFileTargetState,
  onToggleMeaningPanel,
  onCloseMeaningPanel,
}: Props) => {
  const logSender = useViewLogSender(view);
  const commandManager = useCommandManager();
  const viewOperation = useViewOperation();

  const [isPopupMenuOpen, openPopupMenu, closePopupMenu] = useOpenCloseState();
  const [isOpenAnalysisModal, openAnalysisModal, closeAnalysisModal] = useOpenCloseState();
  const [isResizing, setIsResizing] = useState(false);

  const getTransformedVisibleAreaCenterPoint = useGetTransformedVisibleAreaCenterPoint();

  const viewRect = useMemo(() => new Rect(viewPosition, view.size), [view.size, viewPosition]);

  const handleToggleSelectedViews = useCallback(() => {
    onToggleSelectedViews(view.id);
  }, [onToggleSelectedViews, view.id]);

  const handleSelectSingleView = useHandler((viewId?: ViewId) => {
    onSelectSingleView(viewId ?? view.id);
  });

  const handleClickView = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      if (!stickyModelContentsOperation) return;

      if (isSelected) {
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
          if (multiSelectionMode.isMultiElementsSelectionMode) {
            handleSelectSingleView();
            setMultiSelectionMode(MultiSelectionMode.offMode);
          } else {
            handleToggleSelectedViews();
          }
        } else {
          stickyModelContentsOperation.deselectAll();
          handleSelectSingleView();
          setMultiSelectionMode(MultiSelectionMode.offMode);
        }
      } else {
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
          if (multiSelectionMode.isMultiElementsSelectionMode) {
            stickyModelContentsOperation.deselectAll();
            handleSelectSingleView();
            setMultiSelectionMode(MultiSelectionMode.offMode);
          } else if (multiSelectionMode.isMultiViewsSelectionMode) {
            handleToggleSelectedViews();
          } else if (multiSelectionMode.isReadyForMultiSelection) {
            handleToggleSelectedViews();
            setMultiSelectionMode(MultiSelectionMode.multiViewsSelectionMode);
          } else {
            onDeselectAllViews();
            handleToggleSelectedViews();
            setMultiSelectionMode(MultiSelectionMode.multiViewsSelectionMode);
          }
        } else {
          handleSelectSingleView();
          // すでに選択されたビューがある状態の時は、その後複数選択キーを押しながら他のビューを複数選択できる必要があるため、ここでは複数選択準備モードに移行する
          setMultiSelectionMode(MultiSelectionMode.readyForMultiSelectionMode);
        }
      }
    },
    [
      handleSelectSingleView,
      handleToggleSelectedViews,
      multiSelectionMode,
      stickyModelContentsOperation,
      setMultiSelectionMode,
      isSelected,
      onDeselectAllViews,
    ]
  );

  const handleResizeStart = useCallback(() => setIsResizing(true), []);
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleCopyView = useCallback(async () => {
    const success = await ViewsClipboardOperator.copy(
      viewModel.id,
      new ViewCollection([view]),
      new PositionSet({ [view.id]: viewPosition }),
      null,
      logSender
    );

    if (success) {
      toast.success('コピーしました | Copied');
    } else {
      toast.error('コピーに失敗しました | Copy failed');
    }
  }, [logSender, view, viewModel.id, viewPosition]);

  const handleAnalysisStart = useCallback(() => {
    toast('分析処理を開始しました | Started analysis');
  }, []);

  const viewSVGGRef = useRef<SVGGElement>(null);
  const viewTitleSVGGRef = useRef<SVGGElement>(null);

  const handleExportViewAsImage = useCallback(async () => {
    closePopupMenu();

    if (!viewSVGGRef.current || !viewTitleSVGGRef.current) {
      // eslint-disable-next-line no-console
      console.log('[handleExportViewAsImage] viewSVGGRef or viewTitleSVGGRef is null');
      return;
    }

    toast('画像出力を開始しました | Export image file started');

    if (
      await SVGToImage.exportFromViewParts(
        viewSVGGRef.current,
        viewTitleSVGGRef.current,
        viewRect,
        `${view.name.toReadableName()}.png`
      )
    ) {
      toast.success('画像出力が完了しました | Export image file completed');
    } else {
      toast.error('画像出力に失敗しました | Export image file failed');
    }
  }, [closePopupMenu, view.name, viewRect]);

  const handleAnalysisSuccess = useCallback(
    (payload: MLAPIPayload) => {
      // ビューの複数選択に伴いMLAPIPayload(=ViewClipboardPayload)とViewsClipboardPayloadの間の互換性がなくなったので
      // MLAPIPayloadの値を元に、ViewsClipboardPayloadを作る
      const viewsClipboardPayload = ViewsClipboardPayload.fromViewsAndModelContents({
        sourceViewModelId: payload.sourceViewModelId,
        views: ViewCollection.load([payload.view]),
        viewPositions: PositionSet.load({ [new ViewKey(payload.view.key).id]: viewPosition }),
        modelContents: ModelContentsCollection.load([payload.modelContents]),
      });
      duplicateView(viewsClipboardPayload);
      toast.success('分析処理が完了しました | Finished analysis');
    },
    [duplicateView, viewPosition]
  );

  const handleAnalysisFailure = useCallback(() => {
    toast.error('分析処理に失敗しました | Failed to perform analysis');
  }, []);

  const handleCreatingCommentAdd = useCallback(() => {
    const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(view.getRect(viewPosition));
    onCreatingCommentAdd(model.id, transformedVisibleAreaCenterPoint);
  }, [model.id, onCreatingCommentAdd, getTransformedVisibleAreaCenterPoint, view, viewPosition]);

  const handleCreatingCommentDrag = useCallback(
    (dx: number, dy: number) => {
      onCreatingCommentDrag(model.id, dx, dy);
    },
    [model.id, onCreatingCommentDrag]
  );

  const handleCreatingCommentDragEnd = useCallback(
    () => onCreatingCommentDragEnd(model.id),
    [model.id, onCreatingCommentDragEnd]
  );

  const handleCreatingCommentSubmit = useCallback(
    (creatingModelComment: CreatingModelComment) => onCreatingCommentSubmit(model.id, creatingModelComment),
    [model.id, onCreatingCommentSubmit]
  );

  const handleCreatingCommentCancel = useCallback(
    () => onCreatingCommentCancel(model.id),
    [model.id, onCreatingCommentCancel]
  );

  const handleFocusView = useCallback(() => focusView(view.id), [focusView, view.id]);

  const handleToggleMeaningPanel = () => {
    if (!view.meaningPanelViewId) return;
    onToggleMeaningPanel(view.meaningPanelViewId);
  };

  const handleAddMeaningPanelView = useCallback(async () => {
    const { viewId } = await viewOperation.addMeaningPanelView();
    toast('意味の構造パネルを追加しました | Add Meaning Panel');
    onToggleMeaningPanel(viewId);
  }, [viewOperation, onToggleMeaningPanel]);

  const handleCloseMeaningPanel = () => {
    onCloseMeaningPanel(view.id);
  };

  // ai_verification
  const isDebugMode = useIsDebugMode('ai_verification');
  const [aiVerificationMode, setAiVerificationMode] = useState<'aiText' | 'aiViewModeling' | 'aiZoneComparison' | null>(
    null
  );
  const [zoneIds, setZoneIds] = useState<StickyZoneId[]>([]);
  // ai_verification
  // 上記のモードの兼ね合い上検証フェーズでのみここで宣言
  const handleTwoZonesComparisonSelected = useCallback((zoneIds: StickyZoneId[]) => {
    setAiVerificationMode('aiZoneComparison');
    setZoneIds(zoneIds);
  }, []);

  return (
    <>
      {isLinkableTarget && <ConsistencyLinkTargetFrame viewRect={viewRect} />}
      {/* リサイズ中にviewContentをresizeViewの背面に持ってくるようにしている。 */}
      <Swapper
        swap={isResizing}
        a={
          <>
            {isSelected && (
              <ViewSelectionFrame
                readonly={readonly || view.setting.isLocked}
                viewRect={viewRect}
                onResizeStart={handleResizeStart}
                onResizeEnd={handleResizeEnd}
                getGuardProps={getGuardProps}
              />
            )}
          </>
        }
        b={
          <g transform={viewRect.topLeft().toSVGTranslate()} ref={viewSVGGRef}>
            {/* モデルバージョンの変更によって、必ずコンポーネントが再レンダリングされるように、 key にバージョンを含める */}
            {stickyModelContentsOperation && (
              <ActionLogEventParamsContextProvider modelId={model.id} modelType={model.type}>
                <EditingDescriptionPanelContextProvider>
                  <StickyModelContentsView
                    key={`${model.key.toString()}-${model.version}`}
                    readonly={readonly || view.setting.isLocked}
                    ownerGroupId={ownerGroupId}
                    currentUserProfile={currentUserProfile}
                    viewModel={viewModel}
                    view={view}
                    model={model}
                    isSelected={isSelected}
                    getViewRectOperations={getViewRectOperations}
                    onSelectSingleView={handleSelectSingleView}
                    onToggleSelectedViews={handleToggleSelectedViews}
                    onDeselectAllViews={onDeselectAllViews}
                    setMultiSelectionMode={setMultiSelectionMode}
                    onDeleteView={openDeleteModal}
                    onClickViewModelLink={onClickViewModelLink}
                    showNodeCreatedUser={view.setting.showNodeCreatedUser}
                    windowEventManager={windowEventManager}
                    multiSelectionMode={multiSelectionMode}
                    commandManager={commandManager}
                    canvasSize={viewRect.size}
                    logSender={logSender}
                    modelCommentThreadId={modelCommentThreadId}
                    operation={stickyModelContentsOperation}
                    creatingComment={creatingComment}
                    onCreatingCommentAdd={handleCreatingCommentAdd}
                    onCreatingCommentDrag={handleCreatingCommentDrag}
                    onCreatingCommentDragEnd={handleCreatingCommentDragEnd}
                    onCreatingCommentCancel={handleCreatingCommentCancel}
                    onCreatingCommentSubmit={handleCreatingCommentSubmit}
                    onZoneToView={onZoneToView}
                    onAnalysisStart={handleAnalysisStart}
                    onAnalysisSuccess={handleAnalysisSuccess}
                    onAnalysisFailure={handleAnalysisFailure}
                    onCopyView={handleCopyView}
                    getTransformedVisibleAreaCenterPoint={getTransformedVisibleAreaCenterPoint}
                    viewRect={view.getRect(viewPosition)}
                    getGuardProps={getGuardProps}
                    dispatch={dispatch}
                    dragFileTargetState={dragFileTargetState}
                    onTwoZonesComparisonSelected={handleTwoZonesComparisonSelected}
                    onClickView={handleClickView}
                  />
                </EditingDescriptionPanelContextProvider>
              </ActionLogEventParamsContextProvider>
            )}
          </g>
        }
      />

      <ViewTitleBarTeleporter.Source>
        <g ref={viewTitleSVGGRef}>
          {view.viewType === 'meaningPanel' ? (
            <MeaningPanelTitleBar
              viewRect={viewRect}
              width={viewRect.width}
              onClickCloseButton={handleCloseMeaningPanel}
            />
          ) : (
            <>
              <ViewTitleBar
                viewModelId={viewModel.id}
                readonly={readonly}
                hasMeaningPanel={!!view.meaningPanelViewId}
                viewIsLocked={view.setting.isLocked}
                viewIsPositionFixed={view.setting.isPositionFixed}
                viewRect={viewRect}
                name={view.name}
                viewId={view.id}
                onClick={handleClickView}
                onCopyView={handleCopyView}
                onOpenPopupMenu={() => {
                  openPopupMenu();
                  handleSelectSingleView();
                }}
                onDragStart={onDragStartView}
                onDrag={onDragView}
                onDragEnd={onDragEndView}
                onAddMeaningPanel={handleAddMeaningPanelView}
                onToggleMeaningPanel={handleToggleMeaningPanel}
                viewThemeColor={view.themeColor}
                getGuardProps={getGuardProps}
              />
              <ViewPopupMenu
                isOpen={isPopupMenuOpen}
                viewRect={viewRect}
                viewModelId={viewModel.id}
                viewId={view.id}
                groupId={ownerGroupId}
                viewSetting={view.setting}
                onDelete={openDeleteModal}
                onFocusView={handleFocusView}
                onAnalysisSelect={openAnalysisModal}
                onClose={closePopupMenu}
                onExportViewAsImage={handleExportViewAsImage}
                viewThemeColor={view.themeColor}
              />
            </>
          )}
        </g>
      </ViewTitleBarTeleporter.Source>
      <ViewAnalysisSelectModal
        isOpen={isOpenAnalysisModal}
        viewModel={viewModel}
        view={view}
        onStart={handleAnalysisStart}
        onSuccess={handleAnalysisSuccess}
        onFailure={handleAnalysisFailure}
        onClose={closeAnalysisModal}
        onChangeAiVerificationMode={setAiVerificationMode}
      />
      {isDebugMode && (
        <AIVerificationInput
          viewRect={viewRect}
          aiVerificationMode={aiVerificationMode}
          onChangeAiVerificationMode={setAiVerificationMode}
          viewModelId={viewModel.id}
          view={view}
          onSuccess={handleAnalysisSuccess}
          zoneIds={zoneIds}
        />
      )}
      <ClientSelectedItemCollectionLoader viewModelId={viewModel.id} modelId={model.id} />
    </>
  );
};

export const View = memo(ViewFn, propsAreEqual);
